import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx-js-style'; 

const API_BASE_URL = window.location.hostname === 'localhost' 
  ? 'http://localhost:5002' 
  : window.location.origin;

const DistrictDashboard = () => {
    const [data, setData] = useState([]);
    const [viewMode, setViewMode] = useState('summary'); 
    const [selectedDistrict, setSelectedDistrict] = useState(null);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    
    // Modals
    const [actionModalPacs, setActionModalPacs] = useState(null);
    const [showDownloadMenu, setShowDownloadMenu] = useState(false);

    // PENDING REQUEST STATES
    const [showPendingModal, setShowPendingModal] = useState(false);
    const [pendingRequests, setPendingRequests] = useState([]);

    // 🏆 NEW SUPER ADMIN STATES (Live Stats & Modals)
    const [superStats, setSuperStats] = useState({
        totalDistricts: 0,
        totalPacsRegistered: 0,
        totalMembers: 0,
        uploadedPacs: 0,
        zeroUploadDistricts: []
    });
    const [kpiModalConfig, setKpiModalConfig] = useState({ isOpen: false, title: "", dataList: [], type: "" });

    const user = JSON.parse(localStorage.getItem('user')) || { role: 'guest', name: 'User' };
    const stateName = user.stateName || 'UP';
    const authHeaders = { headers: { 'x-state-name': stateName } };

    useEffect(() => {
        if (user.role === 'district_admin') setViewMode('detail');
        fetchDashboardData();
        
        // Fetch live KPI stats if Super/State Admin is on summary page
        if (['super_admin', 'state_admin'].includes(user.role) && viewMode === 'summary') {
            fetchSuperStats();
        }
    }, [viewMode, selectedDistrict, user.role]);

    const fetchDashboardData = async () => {
        setLoading(true);
        try {
            let endpoint = '';
            let payload = {};

            if (['super_admin', 'state_admin'].includes(user.role) && viewMode === 'summary') {
                endpoint = '/api/global-dashboard';
                const res = await axios.get(`${API_BASE_URL}${endpoint}`, authHeaders);
                if (res.data.success) setData(res.data.data);
            } 
            else {
                endpoint = '/api/district-dashboard';
                payload = (user.role === 'district_admin')
                    ? { district_id: user.district_id, stateName }
                    : { district_id: selectedDistrict?.id, stateName };
                const res = await axios.post(`${API_BASE_URL}${endpoint}`, payload, authHeaders);
                if (res.data.success) setData(res.data.data);
            }
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    };

    const fetchSuperStats = async () => {
        try {
            const res = await axios.get(`${API_BASE_URL}/api/admin/super-stats`, authHeaders);
            if (res.data) setSuperStats(res.data);
        } catch (err) { console.error("Stats fetching error:", err); }
    };

    const fetchPendingRequests = async () => {
        try {
            const res = await axios.get(`${API_BASE_URL}/api/admin/pending-requests`, authHeaders);
            setPendingRequests(res.data);
            setShowPendingModal(true);
        } catch (err) { 
            alert("Failed to fetch requests.");
        }
    };

    const handleDeleteData = async (pacs) => {
        const confirmMsg = `⚠️ DANGER ZONE!\n\nAre you sure you want to DELETE ALL DATA for "${pacs.pacs_name}"?\n\nThis will:\n1. Delete all Members, Loans, etc.\n2. Unlock the PACS for Re-upload.\n\nType 'DELETE' to confirm:`;
        const input = prompt(confirmMsg);
        
        if (input === 'DELETE') {
            try {
                await axios.post(`${API_BASE_URL}/api/admin/delete-data`, { pacs_id: pacs.pacs_id, stateName }, authHeaders);
                alert("✅ Data Deleted Successfully! PACS is now Unlocked.");
                setShowDownloadMenu(false);
                setActionModalPacs(null);
                fetchDashboardData();
                if(showPendingModal) fetchPendingRequests(); 
                if(viewMode === 'summary') fetchSuperStats();
            } catch (err) {
                alert("❌ Error: " + (err.response?.data?.error || "Delete failed"));
            }
        }
    };

    const totals = useMemo(() => ({
        pacs: viewMode === 'summary' ? data.reduce((acc, curr) => acc + (curr.total_pacs || 0), 0) : data.length,
        members: data.reduce((acc, curr) => acc + (curr.total_members || 0), 0),
        uploaded: data.filter(i => i.total_members > 0).length
    }), [data, viewMode]);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount || 0);
    };

    const handleDownloadRawData = () => {
        if (!actionModalPacs) return;
        // 🆕 stateName passed in URL because window.open() strips custom headers
        window.open(`${API_BASE_URL}/api/download-pacs-report?pacs_id=${actionModalPacs.pacs_id}&pacs_name=${actionModalPacs.pacs_name}&stateName=${stateName}`);
        setShowDownloadMenu(false);
        setActionModalPacs(null);
    };

    const handleDownloadFormattedTemplate = (tableName) => {
        if (!actionModalPacs) return;
        // 🆕 stateName passed in URL because window.open() strips custom headers
        window.open(`${API_BASE_URL}/api/admin/download-formatted/${encodeURIComponent(actionModalPacs.pacs_id)}/${encodeURIComponent(tableName)}?stateName=${stateName}`, '_blank');
    };

    const handleDownloadSummary = async () => {
        if (!actionModalPacs) return;
        try {
            const res = await axios.get(`${API_BASE_URL}/api/admin/stats/${actionModalPacs.pacs_id}`, authHeaders);
            const dctStats = res.data;
            const wb = XLSX.utils.book_new();
            const reportData = [
                [`DCT-1 Summary Report - ${actionModalPacs.pacs_name}`, ""], 
                ["Particular", "Total"],                   
                ["1. Membership:", ""],
                ["Total Member captured (Count)", dctStats.Membership.Count],
                ["Total Member captured with Zero Share (Count)", dctStats.Membership.ZeroShareCount],
                ["Total Share Deposit Captured (Rs.)", dctStats.Membership.TotalShare],
                ["Total Aadhar Number Captured (Count)", dctStats.Membership.AadharCount],
                ["Total No. of Village Covered (Count)", dctStats.Membership.VillageCount],
                ["2. Short Term Loan:", ""],
                ["Total Loan Member captured (Count)", dctStats.ShortTermLoans.Count],
                ["Total Loan Member captured with zero Principal (Count)", dctStats.ShortTermLoans.ZeroPrinCount],
                ["Total Loan Amount Captured in the Short Term loan(Rs.)", dctStats.ShortTermLoans.TotalAmt],
                ["Total Loan interest Captured in the short Term loan (Rs.)", dctStats.ShortTermLoans.TotalInt],
                ["Total Loan Member captured with zero Interest (Count)", dctStats.ShortTermLoans.ZeroIntCount],
                ["3. Loan Transaction:", ""],
                ["Total Loan Transaction Captured (Count)", dctStats.LoanTransaction.Count],
                ["Total Loan Amount Captured (Rs.)", dctStats.LoanTransaction.TotalAmt],
                ["Total Loan interest Captured (Rs.)", dctStats.LoanTransaction.TotalInt],
                ["4. Land Details:", ""],
                ["Total Land Data Captured (Count)", dctStats.LandDetails.Count],
                ["5. Saving Details:", ""],
                ["Total Saving A/C Data captured (Count)", dctStats.Savings.Count],
                ["Total Saving amount captured (Rs.)", dctStats.Savings.TotalAmt]
            ];
            const ws = XLSX.utils.aoa_to_sheet(reportData);
            const thinBorder = { top: { style: "thin" }, bottom: { style: "thin" }, left: { style: "thin" }, right: { style: "thin" } };
            const headerStyle = { font: { bold: true, sz: 14 }, fill: { fgColor: { rgb: "FFFF00" } }, alignment: { horizontal: "center" }, border: thinBorder };
            const sectionStyle = { font: { bold: true, color: { rgb: "0000FF" } }, border: thinBorder };
            const cellStyle = { border: thinBorder };
            ws['!cols'] = [{ wch: 60 }, { wch: 25 }];
            const range = XLSX.utils.decode_range(ws['!ref']);
            for (let R = range.s.r; R <= range.e.r; ++R) {
                for (let C = range.s.c; C <= range.e.c; ++C) {
                    const cellRef = XLSX.utils.encode_cell({ r: R, c: C });
                    if (!ws[cellRef]) continue;
                    if (R === 0) ws[cellRef].s = headerStyle;
                    else if (ws[XLSX.utils.encode_cell({r: R, c: 0})].v.toString().match(/^\d+\./)) ws[cellRef].s = sectionStyle;
                    else ws[cellRef].s = cellStyle;
                }
            }
            XLSX.utils.book_append_sheet(wb, ws, "DCT-1 Summary");
            XLSX.writeFile(wb, `${actionModalPacs.pacs_name}_DCT_Report.xlsx`);
            setShowDownloadMenu(false);
            setActionModalPacs(null);
        } catch (err) { alert("Error generating report."); }
    };

    const openKpiModal = (title, type) => {
        let list = [];
        if (type === 'districts' || type === 'registered_pacs') {
            list = data.map(d => ({ name: d.district_name, filterKey: d.district_name, sub: 'District' }));
        } else if (type === 'uploaded') {
            list = data.filter(d => d.total_members > 0).map(d => ({ name: d.district_name, filterKey: d.district_name, sub: 'Data Uploaded' }));
        } else if (type === 'zero_districts') {
            list = superStats.zeroUploadDistricts.map(d => ({ name: d, filterKey: d, sub: 'Zero Uploads' }));
        }
        setKpiModalConfig({ isOpen: true, title, dataList: list, type });
    };

    const handleKpiModalClick = (filterValue) => {
        setSearchTerm(filterValue);
        setKpiModalConfig({ ...kpiModalConfig, isOpen: false });
    };

    const styles = {
        wrapper: { minHeight: '100vh', backgroundColor: '#f0f4f8', paddingBottom: '50px' },
        nav: { background: 'linear-gradient(135deg, #4f46e5 0%, #3b82f6 100%)', color: 'white', padding: '15px 40px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, zIndex: 1000, boxShadow: '0 2px 10px rgba(0,0,0,0.1)' },
        navLink: { background: 'rgba(255,255,255,0.18)', color: 'white', padding: '8px 14px', borderRadius: '20px', fontSize: '13px', fontWeight: '600', textDecoration: 'none', border: '1px solid rgba(255,255,255,0.35)', display: 'inline-flex', alignItems: 'center', gap: '6px' },
        card: { background: 'white', padding: '25px', borderRadius: '15px', boxShadow: '0 10px 25px rgba(0,0,0,0.05)', textAlign: 'center', flex: 1 },
        th: { padding: '15px', borderBottom: '2px solid #edf2f7', background: '#f8fafc', fontWeight: 'bold', fontSize: '0.9rem', color: '#64748b' },
        td: { padding: '12px', borderBottom: '1px solid #edf2f7', textAlign: 'center', fontSize: '0.95rem' },
        modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(15, 23, 42, 0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 2000, backdropFilter: 'blur(3px)' },
        modalContent: { background: 'white', padding: '30px', borderRadius: '15px', width: '600px', maxHeight: '80vh', overflowY: 'auto', position: 'relative' },
        actionModalContent: { background: 'white', padding: '20px', borderRadius: '10px', width: '400px', textAlign: 'center', boxShadow: '0 10px 30px rgba(0,0,0,0.2)' },
        actionBtn: { width: '100%', padding: '12px', margin: '8px 0', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }
    };

    return (
        <div style={styles.wrapper}>
            <nav style={styles.nav}>
                <div><h1 style={{ margin: 0, fontSize: '20px', fontWeight: '800' }}>PACS Data Dashboard (v3.10.00)</h1></div>
                <div style={{display: 'flex', gap: '15px', alignItems: 'center'}}>
                    {/* 🆕 In-page cross-navigation links (mirrors the App.js navbar so Assam
                        admins can switch views without going back to login). */}
                    <a href="/dashboard" style={styles.navLink} title="Monitoring Dashboard">📊 Monitoring Dashboard</a>
                    {user.role === 'admin' && (
                        <a href="/admin" style={styles.navLink} title="Admin Panel">🛡️ Admin Panel</a>
                    )}
                    {user.role === 'super_admin' && (
                        <a href="/admin" style={styles.navLink} title="Admin Panel">🛡️ Admin Panel</a>
                    )}
                    <div style={{background: 'rgba(255,255,255,0.2)', padding: '5px 12px', borderRadius: '20px', fontSize: '12px'}}>{selectedDistrict?.name || user.name}</div>

                    {user.role === 'super_admin' && (
                        <button onClick={fetchPendingRequests} style={{background: '#f59e0b', color: 'white', border: 'none', padding: '8px 18px', borderRadius: '25px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.2)', zIndex: 1001, position: 'relative'}}>
                            <i className="fa-solid fa-bell fa-shake"></i> Requests ({pendingRequests.length || 'Check'})
                        </button>
                    )}

                    {viewMode === 'detail' && ['super_admin', 'state_admin'].includes(user.role) && <button onClick={() => setViewMode('summary')} style={{background: 'white', color: '#3b82f6', border: 'none', padding: '5px 15px', borderRadius: '5px', fontWeight: 'bold', cursor: 'pointer'}}>⬅ Back</button>}
                </div>
            </nav>

            <div style={{ maxWidth: '1400px', margin: '30px auto', padding: '0 20px' }}>
                
                {viewMode === 'summary' && ['super_admin', 'state_admin'].includes(user.role) ? (
                    // --- 🏆 SUPER ADMIN LIVE CARDS ---
                    <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px', marginBottom: '30px'}}>
                        <div onClick={() => openKpiModal('Total Districts List', 'districts')} style={{...styles.card, borderLeft: '5px solid #3b82f6', cursor: 'pointer'}}>
                            <h4 style={{margin: '0 0 10px 0', color: '#64748b', fontSize: '0.85rem', textTransform: 'uppercase'}}>Total Districts</h4>
                            <h2 style={{margin: 0, fontSize: '2rem', color: '#1e293b'}}>{superStats.totalDistricts}</h2>
                            <div style={{marginTop: '10px', fontSize: '0.8rem', color: '#3b82f6', fontWeight: 'bold'}}><i className="fa-solid fa-arrow-pointer"></i> Click to Filter</div>
                        </div>

                        <div onClick={() => openKpiModal('Registered PACS Summary', 'registered_pacs')} style={{...styles.card, borderLeft: '5px solid #8b5cf6', cursor: 'pointer'}}>
                            <h4 style={{margin: '0 0 10px 0', color: '#64748b', fontSize: '0.85rem', textTransform: 'uppercase'}}>Total PACS Registered</h4>
                            <h2 style={{margin: 0, fontSize: '2rem', color: '#1e293b'}}>{superStats.totalPacsRegistered}</h2>
                            <div style={{marginTop: '10px', fontSize: '0.8rem', color: '#8b5cf6', fontWeight: 'bold'}}><i className="fa-solid fa-database"></i> View Master List</div>
                        </div>

                        <div style={{...styles.card, borderLeft: '5px solid #f59e0b'}}>
                            <h4 style={{margin: '0 0 10px 0', color: '#64748b', fontSize: '0.85rem', textTransform: 'uppercase'}}>Total Members</h4>
                            <h2 style={{margin: 0, fontSize: '2rem', color: '#1e293b'}}>{superStats.totalMembers.toLocaleString()}</h2>
                            <div style={{marginTop: '10px', fontSize: '0.8rem', color: '#f59e0b', fontWeight: '500'}}><i className="fa-solid fa-users"></i> Live Rows</div>
                        </div>

                        <div onClick={() => openKpiModal('Uploaded PACS List', 'uploaded')} style={{...styles.card, borderLeft: '5px solid #10b981', cursor: 'pointer'}}>
                            <h4 style={{margin: '0 0 10px 0', color: '#64748b', fontSize: '0.85rem', textTransform: 'uppercase'}}>Uploaded PACS ✅</h4>
                            <h2 style={{margin: 0, fontSize: '2rem', color: '#10b981'}}>
                                {superStats.uploadedPacs} <span style={{fontSize: '1.1rem', color: '#64748b', fontWeight: 'normal'}}> / {superStats.totalPacsRegistered}</span>
                            </h2>
                            <div style={{marginTop: '10px', fontSize: '0.8rem', color: '#10b981', fontWeight: 'bold'}}><i className="fa-solid fa-list-check"></i> Click to Filter</div>
                        </div>

                        {/* 🔴 NEW ZERO UPLOAD KPI CARD */}
                        <div onClick={() => openKpiModal('Districts with Zero Uploads', 'zero_districts')} style={{...styles.card, borderLeft: '5px solid #ef4444', cursor: 'pointer'}}>
                            <h4 style={{margin: '0 0 10px 0', color: '#64748b', fontSize: '0.85rem', textTransform: 'uppercase'}}>Zero Upload Districts</h4>
                            <h2 style={{margin: 0, fontSize: '2rem', color: '#ef4444'}}>{superStats.zeroUploadDistricts.length}</h2>
                            <div style={{marginTop: '10px', fontSize: '0.8rem', color: '#ef4444', fontWeight: 'bold'}}><i className="fa-solid fa-triangle-exclamation"></i> Click to View List</div>
                        </div>
                    </div>
                ) : (
                    // --- 📍 DISTRICT ADMIN BASIC CARDS ---
                    <div style={{ display: 'flex', gap: '20px', marginBottom: '30px', flexWrap: 'wrap' }}>
                        <div style={styles.card}><div style={{color: '#718096'}}>Total Branches</div><div style={{fontSize: '28px', fontWeight: 'bold'}}>{data.length}</div></div>
                        <div style={styles.card}><div style={{color: '#718096'}}>Total PACS Registered</div><div style={{fontSize: '28px', fontWeight: 'bold'}}>{totals.pacs}</div></div>
                        <div style={styles.card}><div style={{color: '#718096'}}>Total Members</div><div style={{fontSize: '28px', fontWeight: 'bold', color: '#4f46e5'}}>{totals.members.toLocaleString()}</div></div>
                        <div style={{...styles.card, border: '2px solid #10b981', background: '#f0fdf4'}}>
                            <div style={{color: '#10b981', fontWeight: 'bold'}}>Uploaded PACS ✅</div>
                            <div style={{fontSize: '28px', fontWeight: 'bold', color: '#10b981'}}>{totals.uploaded}</div>
                        </div>
                    </div>
                )}

                <div style={{ background: 'white', padding: '20px', borderRadius: '15px', boxShadow: '0 10px 30px rgba(0,0,0,0.08)' }}>
                    <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '20px', alignItems: 'center', flexWrap: 'wrap', gap: '15px'}}>
                        <h3 style={{margin: 0, color: '#334155'}}>{viewMode === 'summary' ? "District-wise Summary" : "Uploaded PACS Data"}</h3>
                        <div style={{display:'flex', alignItems:'center', gap:'10px'}}>
                            {searchTerm && (
                                <button onClick={() => setSearchTerm("")} style={{background:'#e2e8f0', border:'none', padding:'8px 12px', borderRadius:'8px', cursor:'pointer', fontSize:'0.85rem', fontWeight:'600', color:'#475569'}}>
                                    Clear Filter ✕
                                </button>
                            )}
                            <input type="text" value={searchTerm} placeholder="🔍 Search..." onChange={(e) => setSearchTerm(e.target.value)} style={{padding: '10px', width: '300px', borderRadius: '8px', border: '1px solid #ddd'}} />
                        </div>
                    </div>

                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr>
                                <th style={styles.th}>SR.</th>
                                {viewMode === 'summary' ? (
                                    <><th style={styles.th}>DISTRICT</th><th style={styles.th}>BRANCHES</th><th style={styles.th}>TOTAL PACS</th><th style={styles.th}>MEMBERS</th></>
                                ) : (
                                    <>
                                    <th style={styles.th}>Branch</th>
                                    <th style={styles.th}>Pacs Name</th>
                                    <th style={styles.th}>Status</th> 
                                    <th style={styles.th}>Total Members Captured</th>
                                    <th style={styles.th}>Loanee Member</th>
                                    <th style={styles.th}>Loan Amount (Rs.)</th>
                                    <th style={styles.th}>Transaction Amount (Rs.)</th>
                                     <th style={styles.th}>Total Land Captured</th>
                                     <th style={styles.th}>Action</th>
                                    </>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? <tr><td colSpan="10">Loading...</td></tr> : data.filter(item => {
                                const target = viewMode === 'summary' ? (item.district_name || "") : (item.pacs_name || "");
                                return target.toLowerCase().includes(searchTerm.toLowerCase()) && (viewMode === 'summary' || item.total_members > 0 || item.reupload_request === 1);
                            }).map((item, idx) => (
                                <tr key={idx} style={{background: item.reupload_request === 1 ? '#fff7ed' : 'transparent'}}>
                                    <td style={styles.td}>{idx + 1}</td>
                                    {viewMode === 'summary' ? (
                                        <>
                                            <td style={styles.td}><button onClick={() => {setSelectedDistrict({id: item.district_id, name: item.district_name}); setViewMode('detail');}} style={{background: 'none', border: 'none', color: '#3b82f6', fontWeight: 'bold', textDecoration: 'underline', cursor: 'pointer'}}>{item.district_name}</button></td>
                                            <td style={styles.td}>{item.total_branches}</td>
                                            <td style={styles.td}>{item.total_pacs}</td>
                                            <td style={styles.td}>{item.total_members}</td>
                                        </>
                                    ) : (
                                        <>
                                            <td style={styles.td}>{item.branch_name}</td>
                                            <td style={{...styles.td, textAlign: 'left', fontWeight: 'bold'}}>{item.pacs_name}</td>
                                            
                                            <td style={styles.td}>
                                                {item.reupload_request === 1 ? 
                                                    <span style={{background: '#f97316', color: 'white', padding: '2px 8px', borderRadius: '10px', fontSize: '0.8rem'}}>Request Pending</span> 
                                                    : <span style={{color: '#10b981'}}>Active</span>
                                                }
                                            </td>

                                            <td style={styles.td}>{item.total_members}</td>
                                            <td style={styles.td}>{item.loani_members || 0}</td>
                                            <td style={{...styles.td, color: '#047857', fontWeight: '500'}}>{formatCurrency(item.loan_amount)}</td>
                                            <td style={{...styles.td, color: '#b45309', fontWeight: '500'}}>{formatCurrency(item.transaction_amount)}</td>
                                            <td style={styles.td}>{item.land_count || 0}</td>

                                            <td style={styles.td}>
                                                <button onClick={() => { setActionModalPacs(item); setShowDownloadMenu(false); }} style={{background: '#0d6efd', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold'}}>
                                                    <i className="fa-solid fa-gear me-1"></i> Action
                                                </button>
                                            </td>
                                        </>
                                    )}
                                </tr>
                            ))}
                            {data.filter(item => {
                                const target = viewMode === 'summary' ? (item.district_name || "") : (item.pacs_name || "");
                                return target.toLowerCase().includes(searchTerm.toLowerCase()) && (viewMode === 'summary' || item.total_members > 0 || item.reupload_request === 1);
                            }).length === 0 && (
                                <tr><td colSpan="10" style={{textAlign:'center', padding:'30px', color:'#94a3b8'}}>No records found for your filter.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* 🎯 NEW KPI CLICKABLE MODAL (Filters the dashboard) */}
            {kpiModalConfig.isOpen && (
                <div style={styles.modalOverlay} onClick={() => setKpiModalConfig({...kpiModalConfig, isOpen: false})}>
                    <div style={{...styles.modalContent, width: '520px', padding: 0}} onClick={e => e.stopPropagation()}>
                        <div style={{padding: '20px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                            <div>
                                <h3 style={{margin: 0, color: '#1e293b', fontSize:'1.1rem'}}>{kpiModalConfig.title}</h3>
                                <p style={{margin:'3px 0 0 0', fontSize:'0.75rem', color:'#64748b'}}>Click any entry below to redirect/filter the main dashboard rows.</p>
                            </div>
                            <button onClick={() => setKpiModalConfig({...kpiModalConfig, isOpen: false})} style={{background: '#f1f5f9', border: 'none', width: '32px', height: '32px', borderRadius: '50%', color: '#64748b', cursor: 'pointer', fontWeight: 'bold'}}>✕</button>
                        </div>
                        <div style={{padding: '10px 20px', overflowY: 'auto', maxHeight: '50vh'}}>
                            {kpiModalConfig.dataList.length > 0 ? (
                                <div style={{display:'flex', flexDirection:'column', gap:'4px'}}>
                                    {kpiModalConfig.dataList.map((item, idx) => (
                                        <div key={idx} onClick={() => handleKpiModalClick(item.filterKey)}
                                            style={{ padding: '12px 15px', borderRadius: '8px', borderBottom: '1px solid #f1f5f9', color: '#334155', fontSize: '0.9rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', transition: 'all 0.15s ease' }}
                                            onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#f8fafc'; e.currentTarget.style.color = '#3b82f6'; }}
                                            onMouseOut={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#334155'; }}
                                        >
                                            <div style={{display:'flex', alignItems:'center', gap:'10px'}}><i className="fa-solid fa-magnifying-glass" style={{fontSize: '0.75rem', opacity: 0.4}}></i><span style={{fontWeight:'500'}}>{item.name}</span></div>
                                            <span style={{fontSize:'0.75rem', background:'#f1f5f9', padding:'3px 8px', borderRadius:'12px', color:'#64748b'}}>{item.sub}</span>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div style={{textAlign: 'center', padding: '40px 20px', color: '#94a3b8'}}><p>No entries found.</p></div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* PENDING REQUESTS POPUP */}
            {showPendingModal && (
                <div style={styles.modalOverlay}>
                    <div style={styles.modalContent}>
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                            <h2 style={{margin: 0, color: '#f59e0b'}}><i className="fa-solid fa-bell"></i> Pending Requests</h2>
                            <button onClick={() => setShowPendingModal(false)} style={{background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer'}}>×</button>
                        </div>
                        <hr style={{margin: '15px 0'}} />
                        {pendingRequests.length === 0 ? (
                            <div style={{textAlign: 'center', padding: '30px', color: '#666'}}>No pending requests.</div>
                        ) : (
                            <table style={{width: '100%', borderCollapse: 'collapse'}}>
                                <thead>
                                    <tr style={{background: '#f8fafc', textAlign: 'left'}}>
                                        <th style={{padding: '10px'}}>District</th><th style={{padding: '10px'}}>PACS Name</th><th style={{padding: '10px', textAlign: 'right'}}>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pendingRequests.map((req, i) => (
                                        <tr key={i} style={{borderBottom: '1px solid #eee'}}>
                                            <td style={{padding: '10px'}}>{req.district_name}</td>
                                            <td style={{padding: '10px', fontWeight: 'bold'}}>{req.pacs_name}</td>
                                            <td style={{padding: '10px', textAlign: 'right'}}>
                                                <button onClick={() => handleDeleteData(req)} style={{background: '#ef4444', color: 'white', border: 'none', padding: '8px 12px', borderRadius: '5px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 'bold'}}>
                                                    Approve & Delete
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            )}

            {/* Action Modal (Download & Delete) */}
            {actionModalPacs && (
                <div style={styles.modalOverlay} onClick={() => { setActionModalPacs(null); setShowDownloadMenu(false); }}>
                    <div style={styles.actionModalContent} onClick={(e) => e.stopPropagation()}>
                        <h3 style={{margin: '0 0 20px 0', color: '#333'}}>{showDownloadMenu ? 'Download Final Format' : 'Select Action'}</h3>
                        <p style={{color: '#666', marginBottom: '20px'}}>For: <strong>{actionModalPacs.pacs_name}</strong></p>

                        {!showDownloadMenu ? (
                            <>
                                <button style={{...styles.actionBtn, background: '#10b981', color: 'white'}} onClick={handleDownloadRawData}><i className="fa-solid fa-database"></i> Download Uploaded Data</button>
                                <button style={{...styles.actionBtn, background: '#eab308', color: 'black'}} onClick={handleDownloadSummary}><i className="fa-solid fa-file-invoice"></i> Download Summary Report</button>
                                <button style={{...styles.actionBtn, background: '#2563eb', color: 'white'}} onClick={() => setShowDownloadMenu(true)}><i className="fa-solid fa-file-arrow-down"></i> Download Final Bulk Upload Format</button>
                                {user.role === 'super_admin' && (
                                    <button style={{...styles.actionBtn, background: '#dc2626', color: 'white', marginTop: '15px'}} onClick={() => handleDeleteData(actionModalPacs)}><i className="fa-solid fa-trash-can"></i> Approve & Delete Data</button>
                                )}
                                <button style={{...styles.actionBtn, background: '#f1f5f9', color: '#64748b', marginTop: '15px'}} onClick={() => setActionModalPacs(null)}>Cancel</button>
                            </>
                        ) : (
                            <>
                                <button
                                    style={{...styles.actionBtn, background: '#1d4ed8', color: 'white'}}
                                    onClick={() => handleDownloadFormattedTemplate('Membership')}
                                >
                                    Membership
                                </button>
                                <button
                                    style={{...styles.actionBtn, background: '#2563eb', color: 'white'}}
                                    onClick={() => handleDownloadFormattedTemplate('ShortTermLoans')}
                                >
                                    ShortTermLoans
                                </button>
                                <button
                                    style={{...styles.actionBtn, background: '#3b82f6', color: 'white'}}
                                    onClick={() => handleDownloadFormattedTemplate('LoanTransaction')}
                                >
                                    LoanTransaction
                                </button>
                                <button
                                    style={{...styles.actionBtn, background: '#60a5fa', color: 'white'}}
                                    onClick={() => handleDownloadFormattedTemplate('LandDetails')}
                                >
                                    LandDetails
                                </button>
                                <button
                                    style={{...styles.actionBtn, background: '#93c5fd', color: '#0f172a'}}
                                    onClick={() => handleDownloadFormattedTemplate('Savings')}
                                >
                                    Savings
                                </button>

                                {/* 🆕 Assam-Only: UP will never see or render this. Independent sibling
                                    below the 5 base buttons. Strict structural isolation — only the
                                    conditional renders, no UP fallback path. */}
                                {stateName === 'ASSAM' && (
                                    <button
                                        style={{ ...styles.actionBtn, background: '#f97316', color: '#fff' }}
                                        onClick={() => handleDownloadFormattedTemplate('RecurringDeposit')}
                                    >
                                        RecurringDeposit
                                    </button>
                                )}

                                <button
                                    style={{...styles.actionBtn, background: '#f8fafc', color: '#334155', marginTop: '15px'}}
                                    onClick={() => setShowDownloadMenu(false)}
                                >
                                    Back
                                </button>
                            </>
                        )}
                    </div>
                </div>
            )}
            <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.0/css/all.min.css" />
        </div>
    );
};
export default DistrictDashboard;
